// App.js

import { useAuth } from "./contexts/AuthContext";
import { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import Home from "./pages/Home/Home";
import RequestCoin from "./pages/RequestCoin/RequestCoin";
import UserProfile from "./pages/UserProfile/UserProfile";
import UserCoins from "./pages/UserCoins/UserCoins";
import SentCoins from "./pages/SentCoins/SentCoins";
import CoinDetail from "./pages/CoinDetail/CoinDetail";
import MyOrganizations from "./pages/MyOrganizations/MyOrganizations";
import OrganizationDetail from "./pages/OrganizationDetail/OrganizationDetail";
import Settings from "./pages/Settings/Settings";
import ReportBug from "./pages/ReportBug/ReportBug";
import BugReportsViewer from "./pages/BugReportsViewer/BugReportsViewer";
import PendingRequests from "./pages/PendingRequests/PendingRequests";
import TermsAndConditions from "./pages/TermsAndConditions/TermsAndConditions";
import Login from "./pages/Login/Login";
import Signup from "./pages/Signup/Signup";
import VerifyEmail from "./pages/VerifyEmail/VerifyEmail";
import ForgotPassword from "./pages/ForgotPassword/ForgotPassword";
import Layout from "./components/layout/Layout/Layout";
import ScrollToTop from "./components/common/ScrollToTop";
import apiService from "./services/api";
import "./App.css";

function App() {
  const auth = useAuth();
  const [userCreated, setUserCreated] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);
  const [userError, setUserError] = useState(null);

  // No OIDC errors to handle anymore with direct Cognito SDK

  // Create user in backend after Cognito authentication
  useEffect(() => {
    const createBackendUser = async () => {
      // Only proceed if authenticated and haven't created user yet
      if (!auth.isAuthenticated || userCreated || creatingUser) {
        return;
      }

      // Get the ID token from our auth context
      const idToken = auth.user?.id_token;
      if (!idToken) {
        console.warn('No ID token available yet');
        return;
      }

      setCreatingUser(true);
      setUserError(null);

      try {
        // Create or verify user exists in backend
        await apiService.createUser(auth.user, idToken);
        setUserCreated(true);
        console.log('User successfully created/verified in backend');
      } catch (error) {
        console.error('Failed to create user in backend:', error);
        setUserError(error.message);
      } finally {
        setCreatingUser(false);
      }
    };

    createBackendUser();
  }, [auth.isAuthenticated, auth.user, userCreated, creatingUser]);

  if (auth.isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // No auth.error with our custom auth context

  // Show loading while creating user in backend
  if (auth.isAuthenticated && creatingUser) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Setting up your account...</p>
      </div>
    );
  }

  // Show error if user creation failed
  if (auth.isAuthenticated && userError && !userCreated) {
    return (
      <div className="error-container">
        <h2>Account Setup Error</h2>
        <p>{userError}</p>
        <button onClick={() => {
          setUserError(null);
          setUserCreated(false);
          setCreatingUser(false);
        }}>Retry</button>
        <button onClick={() => auth.signOut()} style={{ marginLeft: '10px' }}>
          Sign Out
        </button>
      </div>
    );
  }

  // Always render router to handle both auth and app routes
  return (
    <Router>
      <ScrollToTop />
      <Routes>
        {/* Public routes - accessible without authentication */}
        <Route path="/transaction/:txId" element={<CoinDetail />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />

        {/* Protected routes - require authentication */}
        {auth.isAuthenticated ? (
          <>
            {/* Terms page - no Layout wrapper to avoid redirect loop */}
            <Route path="/terms" element={<TermsAndConditions />} />

            {/* App routes - all wrapped in Layout which checks terms acceptance */}
            <Route path="/" element={<Layout><Home /></Layout>} />
            <Route path="/request-coin" element={<Layout><RequestCoin /></Layout>} />
            <Route path="/my-organizations" element={<Layout><MyOrganizations /></Layout>} />
            <Route path="/organization/:orgId" element={<Layout><OrganizationDetail /></Layout>} />
            <Route path="/profile/:userId" element={<Layout><UserProfile /></Layout>} />
            <Route path="/profile/:userId/coins" element={<Layout><UserCoins /></Layout>} />
            <Route path="/profile/:userId/sent-coins" element={<Layout><SentCoins /></Layout>} />
            <Route path="/settings" element={<Layout><Settings /></Layout>} />
            <Route path="/report-bug" element={<Layout><ReportBug /></Layout>} />
            <Route path="/bug-reports" element={<Layout><BugReportsViewer /></Layout>} />
            <Route path="/pending-requests" element={<Layout><PendingRequests /></Layout>} />
          </>
        ) : null}

        {/* Catch-all redirect */}
        <Route path="*" element={<Navigate to={auth.isAuthenticated ? "/" : "/login"} replace />} />
      </Routes>
    </Router>
  );
}

export default App;
